import React, { useEffect, useState } from "react";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import LoaderSpinner from "../common/Loader";
import InputCalculator from "../common/InputCalculator";
import { normalizeCalculatorValue, hasUnevaluatedExpression } from "../common/calculatorValue";
import { fetchWalletThresholdDefaults, updateWalletThresholdDefaults } from "../../store/actions";
import { checkPermission } from "../../config/utils";
import { featureSubmodule, settings, SUBF_DEFAULTSETTINGS } from "../../constant";

const DEFAULT_FORM = {
    minimumBalance: 0,
    lowBalanceAlertThreshold: 0,
    blockLabelPurchaseBelowMin: true,
    sendLowBalanceEmailNotification: true,
    processFeePercent: 0,
};

// Values coerced through `Number("")` become NaN — treat blank as 0 for validation.
const toNumber = (val) => {
    if (val === "" || val === null || val === undefined) return 0;
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
};

const readEntity = (payload) => payload?.Entity ?? payload?.entity ?? null;

const extractStatus = (response) => response?.data?.Status ?? response?.data?.status;

const extractMessage = (response) =>
    response?.data?.Message?.AppStatusDescription
    ?? response?.data?.message
    ?? '';

export default function WalletThresholdDefaults(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userDetails = useSelector((state) => state.auth.user);
    const employeePermissions = useSelector((state) => state?.sidebar?.employeePermissions);
    const permissionEmployees = props?.employeePermissions || employeePermissions;
    const { walletThresholdDefaults, walletThresholdDefaultsStatus } =
        useSelector((state) => state.DefaultConfigReducer);

    const hasAccess = userDetails?.Value?.isSuperAdmin
        && checkPermission(settings, SUBF_DEFAULTSETTINGS, '', featureSubmodule, permissionEmployees);

    const [form, setForm] = useState(DEFAULT_FORM);
    const [initialForm, setInitialForm] = useState(DEFAULT_FORM);
    const [editable, setEditable] = useState(false);
    const [isFormEdited, setIsFormEdited] = useState(false);
    const [error, setError] = useState({});
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        if (userDetails?.Value && !hasAccess) {
            navigate("/setting");
        }
    }, [userDetails, hasAccess, navigate]);

    useEffect(() => {
        if (hasAccess) {
            dispatch(fetchWalletThresholdDefaults());
        }
    }, [hasAccess, dispatch]);

    useEffect(() => {
        setShowLoader(walletThresholdDefaultsStatus === 'pending');
    }, [walletThresholdDefaultsStatus]);

    useEffect(() => {
        const entity = readEntity(walletThresholdDefaults);
        if (entity) {
            const seeded = {
                minimumBalance: entity.minimumBalance ?? entity.MinimumBalance ?? 0,
                lowBalanceAlertThreshold: entity.lowBalanceAlertThreshold ?? entity.LowBalanceAlertThreshold ?? 0,
                blockLabelPurchaseBelowMin: entity.blockLabelPurchaseBelowMin ?? entity.BlockLabelPurchaseBelowMin ?? true,
                sendLowBalanceEmailNotification: entity.sendLowBalanceEmailNotification ?? entity.SendLowBalanceEmailNotification ?? true,
                processFeePercent: entity.processFeePercent ?? entity.ProcessFeePercent ?? 0,
            };
            setForm(seeded);
            setInitialForm(seeded);
            setIsFormEdited(false);
            setError({});
        }
    }, [walletThresholdDefaults]);

    const updateForm = (patch) => {
        setForm((prev) => {
            const next = { ...prev, ...patch };
            const changed = Object.keys(next).some((k) => String(next[k]) !== String(initialForm[k]));
            setIsFormEdited(changed);
            return next;
        });
    };

    const handleAmountChange = (name, value) => {
        const validDecimalNum = /^(\d+\.?\d{0,2}|\.\d{0,2}|\.)$|^$/;
        const isExpression = /[^\d.]/.test(value);
        if (!isExpression && !validDecimalNum.test(value)) return;
        updateForm({ [name]: value });
        setError((prev) => ({ ...prev, [name]: undefined }));
    };

    const handleAmountInput = (name) => (e) => {
        const value = normalizeCalculatorValue(e);
        if (value !== null) handleAmountChange(name, value);
    };

    // `value` is the calculator's evaluated text; without it the stored form value is used.
    const handleAmountBlur = (name, value) => {
        const raw = value === undefined ? form[name] : value;
        if (value !== undefined && raw !== '.' && Number.isNaN(Number(raw))) return;
        const n = toNumber(raw);
        updateForm({ [name]: n.toFixed(2) });
    };

    const validate = () => {
        const errs = {};
        const min = toNumber(form.minimumBalance);
        const alertAt = toNumber(form.lowBalanceAlertThreshold);
        const fee = toNumber(form.processFeePercent);
        if (min < 0) errs.minimumBalance = 'Minimum wallet balance cannot be negative';
        if (alertAt < 0) errs.lowBalanceAlertThreshold = 'Low balance alert cannot be negative';
        if (!errs.lowBalanceAlertThreshold && alertAt < min) {
            errs.lowBalanceAlertThreshold = 'Low balance alert must be greater than or equal to minimum wallet balance';
        }
        if (fee < 0) errs.processFeePercent = 'Processing fee cannot be negative';
        ['minimumBalance', 'lowBalanceAlertThreshold', 'processFeePercent'].forEach((key) => {
            if (!errs[key] && hasUnevaluatedExpression(form[key])) errs[key] = 'Enter a valid number';
        });
        setError(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        const payload = {
            minimumBalance: toNumber(form.minimumBalance),
            lowBalanceAlertThreshold: toNumber(form.lowBalanceAlertThreshold),
            blockLabelPurchaseBelowMin: !!form.blockLabelPurchaseBelowMin,
            sendLowBalanceEmailNotification: !!form.sendLowBalanceEmailNotification,
            processFeePercent: toNumber(form.processFeePercent),
        };
        setShowLoader(true);
        try {
            const response = await Promise.resolve(dispatch(updateWalletThresholdDefaults(payload)));
            const status = extractStatus(response);
            if (status === 200) {
                toast.success('Default wallet threshold settings saved successfully');
                setEditable(false);
                setIsFormEdited(false);
                dispatch(fetchWalletThresholdDefaults());
            } else {
                toast.error(extractMessage(response) || 'Failed to save default wallet threshold settings');
            }
        } catch {
            toast.error('An error occurred while saving default wallet threshold settings');
        }
        setShowLoader(false);
    };

    const handleCancel = () => {
        setForm(initialForm);
        setError({});
        setIsFormEdited(false);
        setEditable(false);
    };

    if (!hasAccess) return null;

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    Wallet Threshold
                    <div className="top_right_button_container">
                        <Button
                            className="cus-primary-transparent-btn"
                            onClick={() => navigate("/default-settings")}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                <div className="container-fluid">
                    <div className="cus-page-content-sec">
                        <div className="d-flex justify-content-between align-items-center pl-3 pr-3 pt-3">
                            <h5 className="mb-0">Default Wallet Threshold Settings</h5>
                            <div className="top_right_button_container">
                                {editable && (
                                    <button
                                        type="button"
                                        className="btn cus-primary-transparent-btn top-btn"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn cus-seconday-bg-btn top-btn editBtn"
                                    onClick={() => setEditable(true)}
                                    disabled={editable}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={handleSave}
                                    disabled={!editable || !isFormEdited}
                                >
                                    Save
                                </button>
                            </div>
                        </div>

                        <div className="mb-3 pl-3 pr-3 mt-3">
                            <div className="alert alert-info" role="alert" style={{ fontSize: '13px' }}>
                                <i className="fa fa-info-circle me-2 pr-1" aria-hidden="true"></i>These settings apply only to newly created seller accounts. Existing seller configurations are not affected.
                            </div>
                        </div>

                        {showLoader && <LoaderSpinner />}

                        <div className="pl-3 pr-3 pb-3">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="minimumBalance">
                                        <strong>Minimum wallet balance ($)</strong>
                                    </label>
                                    <InputCalculator
                                        id="minimumBalance"
                                        inputMode="decimal"
                                        maxLength={50}
                                        value={String(form.minimumBalance ?? '')}
                                        onChange={handleAmountInput('minimumBalance')}
                                        onBlur={(e) => handleAmountBlur('minimumBalance', e.target.value)}
                                        safeBlur
                                        disabled={!editable}
                                        percentMode
                                    />
                                    {error.minimumBalance && (
                                        <div className="text-danger" style={{ fontSize: '12px' }}>{error.minimumBalance}</div>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="lowBalanceAlertThreshold">
                                        <strong>Low balance alert at ($)</strong>
                                    </label>
                                    <InputCalculator
                                        id="lowBalanceAlertThreshold"
                                        inputMode="decimal"
                                        maxLength={50}
                                        value={String(form.lowBalanceAlertThreshold ?? '')}
                                        onChange={handleAmountInput('lowBalanceAlertThreshold')}
                                        onBlur={(e) => handleAmountBlur('lowBalanceAlertThreshold', e.target.value)}
                                        safeBlur
                                        disabled={!editable}
                                        percentMode
                                    />
                                    {error.lowBalanceAlertThreshold && (
                                        <div className="text-danger" style={{ fontSize: '12px' }}>{error.lowBalanceAlertThreshold}</div>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label" htmlFor="processFeePercent">
                                        <strong>Wallet Recharge Processing Fee (%)</strong>
                                    </label>
                                    <InputCalculator
                                        id="processFeePercent"
                                        inputMode="decimal"
                                        maxLength={50}
                                        value={String(form.processFeePercent ?? '')}
                                        onChange={handleAmountInput('processFeePercent')}
                                        onBlur={(e) => handleAmountBlur('processFeePercent', e.target.value)}
                                        safeBlur
                                        disabled={!editable}
                                    />
                                    {error.processFeePercent && (
                                        <div className="text-danger" style={{ fontSize: '12px' }}>{error.processFeePercent}</div>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center p-3 mb-2"
                                style={{ background: '#f5f7fa', borderRadius: '6px' }}>
                                <strong>Block label purchase below minimum balance</strong>
                                <Switch
                                    onChange={(checked) => updateForm({ blockLabelPurchaseBelowMin: checked })}
                                    checked={!!form.blockLabelPurchaseBelowMin}
                                    disabled={!editable}
                                    className="switchBtn"
                                />
                            </div>

                            <div className="d-flex justify-content-between align-items-center p-3"
                                style={{ background: '#f5f7fa', borderRadius: '6px' }}>
                                <strong>Send low balance email notification</strong>
                                <Switch
                                    onChange={(checked) => updateForm({ sendLowBalanceEmailNotification: checked })}
                                    checked={!!form.sendLowBalanceEmailNotification}
                                    disabled={!editable}
                                    className="switchBtn"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

WalletThresholdDefaults.propTypes = {
    employeePermissions: PropTypes.array,
};
